/**
 * REST API Response Validators
 *
 * Typia validators for all REST API response types.
 * These ensure runtime type safety and can coerce values to expected types.
 *
 * Usage:
 * ```typescript
 * import { validateAgentDTO, validatePaginatedAgents } from '@ops-matrix/shared-types';
 *
 * const agent = validateAgentDTO(apiResponse.data);
 * const agents = validatePaginatedAgents(apiResponse.data);
 * ```
 *
 * @module validators/rest-api
 */

import { z } from 'zod';
import type {
  AgentDTO,
  AuthTokenResponse,
  DocumentDTO,
  ErrorResponse,
  PaginatedResponse,
  UserDTO,
} from '../contracts/rest-api.contract';

/**
 * Validates Agent DTO responses
 * Ensures response matches AgentDTO interface with type coercion
 *
 * @param data Unknown data to validate
 * @returns Validated AgentDTO
 * @throws ZodError if validation fails
 */
export const AgentDTOValidator = z
  .object({
    id: z.string().uuid(),
    name: z.string().min(1).max(255),
    description: z.string().max(1024).optional(),
    model_id: z.string().min(1),
    status: z.enum(['ACTIVE', 'INACTIVE', 'FAILED']),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
  })
  .strict();

export function validateAgentDTO(data: unknown): AgentDTO {
  return AgentDTOValidator.parse(data);
}

/**
 * Validates paginated agent responses
 *
 * @param data Unknown data to validate
 * @returns Validated PaginatedResponse<AgentDTO>
 * @throws ZodError if validation fails
 */
export const PaginatedAgentsValidator = z
  .object({
    data: z.array(AgentDTOValidator),
    pagination: z.object({
      page: z.number().int().min(0),
      limit: z.number().int().min(1),
      total: z.number().int().min(0),
      has_next: z.boolean(),
    }),
  })
  .strict();

export function validatePaginatedAgents(data: unknown): PaginatedResponse<AgentDTO> {
  return PaginatedAgentsValidator.parse(data);
}

/**
 * Validates Document DTO responses
 *
 * @param data Unknown data to validate
 * @returns Validated DocumentDTO
 * @throws ZodError if validation fails
 */
export const DocumentDTOValidator = z
  .object({
    id: z.string().uuid(),
    knowledge_base_id: z.string().uuid(),
    title: z.string().min(1).max(255),
    content: z.string().min(1),
    status: z.enum(['INDEXED', 'INDEXING', 'FAILED']),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
  })
  .strict();

export function validateDocumentDTO(data: unknown): DocumentDTO {
  return DocumentDTOValidator.parse(data);
}

/**
 * Validates paginated document responses
 *
 * @param data Unknown data to validate
 * @returns Validated PaginatedResponse<DocumentDTO>
 * @throws ZodError if validation fails
 */
export const PaginatedDocumentsValidator = z
  .object({
    data: z.array(DocumentDTOValidator),
    pagination: z.object({
      page: z.number().int().min(0),
      limit: z.number().int().min(1),
      total: z.number().int().min(0),
      has_next: z.boolean(),
    }),
  })
  .strict();

export function validatePaginatedDocuments(data: unknown): PaginatedResponse<DocumentDTO> {
  return PaginatedDocumentsValidator.parse(data);
}

/**
 * Validates User DTO responses
 *
 * @param data Unknown data to validate
 * @returns Validated UserDTO
 * @throws ZodError if validation fails
 */
export const UserDTOValidator = z
  .object({
    id: z.string().uuid(),
    email: z.string().email(),
    display_name: z.string().min(1).max(255),
    role: z.enum(['ADMIN', 'USER', 'MODERATOR']),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
  })
  .strict();

export function validateUserDTO(data: unknown): UserDTO {
  return UserDTOValidator.parse(data);
}

/**
 * Validates Auth Token responses
 *
 * @param data Unknown data to validate
 * @returns Validated AuthTokenResponse
 * @throws ZodError if validation fails
 */
export const AuthTokenResponseValidator = z
  .object({
    access_token: z.string().min(1),
    refresh_token: z.string().min(1),
    expires_in: z.number().int().positive(),
    token_type: z.literal('Bearer'),
  })
  .strict();

export function validateAuthTokenResponse(data: unknown): AuthTokenResponse {
  return AuthTokenResponseValidator.parse(data);
}

/**
 * Validates Error responses
 *
 * @param data Unknown data to validate
 * @returns Validated ErrorResponse
 * @throws ZodError if validation fails
 */
export const ErrorResponseValidator = z
  .object({
    error: z.object({
      code: z.string(),
      message: z.string(),
      details: z.record(z.string(), z.unknown()).optional(),
    }),
  })
  .strict();

export function validateErrorResponse(data: unknown): ErrorResponse {
  return ErrorResponseValidator.parse(data);
}

/**
 * Type-safe validator for API responses with discriminated unions
 * Automatically determines if response is success or error
 *
 * @param data Unknown data to validate
 * @returns Validated response (success or error)
 * @throws ZodError if validation fails
 */
export const APIResponseValidator = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('success'),
    status: z.number(),
    data: z.unknown(),
  }),
  z.object({
    type: z.literal('error'),
    status: z.number(),
    error: z.object({
      code: z.string(),
      message: z.string(),
      details: z.record(z.string(), z.unknown()).optional(),
    }),
  }),
]);

/**
 * Type guard to check if response is an error
 *
 * @param response API response to check
 * @returns true if response is an error
 */
export function isAPIErrorResponse(response: unknown): response is ErrorResponse {
  try {
    ErrorResponseValidator.parse(response);
    return true;
  } catch {
    return false;
  }
}

/**
 * Type guard to check if data is a valid AgentDTO
 *
 * @param data Unknown data to check
 * @returns true if data is AgentDTO
 */
export function isAgentDTO(data: unknown): data is AgentDTO {
  try {
    AgentDTOValidator.parse(data);
    return true;
  } catch {
    return false;
  }
}

/**
 * Type guard to check if data is a valid DocumentDTO
 *
 * @param data Unknown data to check
 * @returns true if data is DocumentDTO
 */
export function isDocumentDTO(data: unknown): data is DocumentDTO {
  try {
    DocumentDTOValidator.parse(data);
    return true;
  } catch {
    return false;
  }
}

/**
 * Type guard to check if data is a valid UserDTO
 *
 * @param data Unknown data to check
 * @returns true if data is UserDTO
 */
export function isUserDTO(data: unknown): data is UserDTO {
  try {
    UserDTOValidator.parse(data);
    return true;
  } catch {
    return false;
  }
}
